<template>
  <div class="ds-page">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Wage Payment System</div>
          <h2 class="page-title">WPS Invoices</h2>
          <p class="page-subtitle">
            Review invoice summaries and generate billing for
            <strong>{{ routeCompanyName }}</strong
            >.
          </p>
        </div>
        <div class="col-auto ms-auto">
          <button class="ds-btn ds-btn--primary" disabled title="Pending product confirmation">
            <font-awesome-icon icon="plus" />
            Create Invoice
          </button>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-card__body">
        <div class="wps-filter-header">
          <span class="ds-filters__label">Filters</span>
          <span class="ds-filters__hint">Search invoice rows by company client, filter invoice status, and control sorting.</span>
        </div>

        <div class="wps-filter-two-col">
          <div class="wps-filter-left">
            <div class="wps-filter-grid-left">
              <div class="ds-field">
                <label class="ds-field__label">Invoice Status</label>
                <el-select v-model="filters.invoice_status" size="small" class="w-100" placeholder="Select status">
                  <el-option v-for="item in invoiceStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Sort By</label>
                <el-select v-model="filters.sort_by" size="small" class="w-100" placeholder="Sort by">
                  <el-option v-for="item in sortByOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Order</label>
                <el-select v-model="filters.sort_type" size="small" class="w-100" placeholder="Order">
                  <el-option v-for="item in sortTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>
            </div>
          </div>

          <div class="wps-filter-sep"></div>

          <div class="wps-filter-right">
            <div class="wps-filter-grid-right">
              <div class="ds-field">
                <label class="ds-field__label">Search By</label>
                <el-select v-model="filters.search_by" size="small" class="w-100" placeholder="Select search">
                  <el-option v-for="item in searchByOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Search Value</label>
                <el-input
                  v-model="filters.search_value"
                  clearable
                  size="small"
                  placeholder="Input company client name"
                  @clear="searchHandler"
                />
              </div>
            </div>

            <div class="wps-filter-actions">
              <button @click="resetHandler" class="ds-btn ds-btn--secondary ds-btn--sm">Reset</button>
              <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="is_loading">
          <thead>
            <tr>
              <th>Invoice Number</th>
              <th>Company Client</th>
              <th>Status</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Admin Fee</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Due Date</th>
              <th>Paid At</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in list" :key="i">
              <td>
                <span class="ds-text-bold">{{ item.invoice_number || "—" }}</span>
              </td>
              <td>{{ item.company_client_name }}</td>
              <td>
                <span class="ds-pill" :class="invoiceStatusClass(item.invoice_status)">
                  {{ invoiceStatusLabel(item.invoice_status) }}
                </span>
              </td>
              <td class="text-right">
                <span class="ds-amount">{{ item.amount_str }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount--muted">{{ item.admin_fee_str }}</span>
              </td>
              <td>{{ item.start_date_str }}</td>
              <td>{{ item.end_date_str }}</td>
              <td>{{ item.due_date_str }}</td>
              <td>{{ item.paid_at_str }}</td>
              <td class="text-center">
                <div class="ds-row-actions">
                  <el-tooltip content="View invoice items" effect="dark" placement="top">
                    <el-button round size="mini" class="wps-action-view" circle @click="handlerBtnDetailInvoice(item)">
                      <font-awesome-icon icon="address-book" />
                    </el-button>
                  </el-tooltip>
                  <el-tooltip v-if="item.invoice_url" content="Preview invoice" effect="dark" placement="top">
                    <el-button round size="mini" class="wps-action-view" circle @click="handlerBtnViewInvoice(item)">
                      <font-awesome-icon icon="eye" />
                    </el-button>
                  </el-tooltip>
                  <el-tooltip v-if="item.invoice_url" content="Download invoice" effect="dark" placement="top">
                    <el-button round size="mini" class="wps-action-download" circle @click="handlerDownloadInvoice(item)">
                      <font-awesome-icon icon="download" />
                    </el-button>
                  </el-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <el-empty description="No invoice" v-if="!total_rows && !is_loading" :image="emptyStateImage" class="ds-empty" />
      </div>

      <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>

    <b-modal size="lg" v-model="show.modal_invoice" title="Invoice Preview" hide-footer>
      <div class="w-100 h-100">
        <vue-pdf-embed :source="selected_invoice" />
      </div>
    </b-modal>
  </div>
</template>
<script>
import VuePdfEmbed from "vue-pdf-embed/dist/vue2-pdf-embed";
import moment from "moment";
import { WPS_INVOICE_LIST_PER_COMPANY } from "@/store/modules/wps";
import emptyStateImage from "@/assets/images/undraw/empty-approval-history.svg";
import Pagination from "../../components/Pagination.vue";
import url from "url";

const invoice_status = {
  DRAFT: {
    label: "Draft",
    color: "ds-pill--gray",
  },
  PAID: {
    label: "Paid",
    color: "ds-pill--success",
  },
  OVERDUE: {
    label: "Overdue",
    color: "ds-pill--danger",
  },
  CANCELLED: {
    label: "Cancelled",
    color: "ds-pill--danger",
  },
};

export default {
  components: {
    Pagination,
    VuePdfEmbed,
  },
  name: "InvoiceListWps",
  data() {
    return {
      emptyStateImage,
      is_loading: false,
      total_rows: 0,
      filters: {
        search_by: "company_client_name",
        search_value: "",
        invoice_status: "",
        sort_by: "created_at",
        sort_type: "desc",
      },
      searchByOptions: [{ value: "company_client_name", label: "Company Client" }],
      invoiceStatusOptions: [
        { value: "", label: "All" },
        { value: "DRAFT", label: "Draft" },
        { value: "PAID", label: "Paid" },
        { value: "OVERDUE", label: "Overdue" },
        { value: "CANCELLED", label: "Cancelled" },
      ],
      sortByOptions: [
        { value: "created_at", label: "Created At" },
        { value: "invoice_number", label: "Invoice Number" },
        { value: "amount", label: "Amount" },
        { value: "admin_fee", label: "Admin Fee" },
        { value: "due_date", label: "Due Date" },
        { value: "paid_at", label: "Paid At" },
      ],
      sortTypeOptions: [
        { value: "desc", label: "Descending" },
        { value: "asc", label: "Ascending" },
      ],
      page: 1,
      per_page: 20,
      list: [],
      show: {
        modal_invoice: false,
      },
      selected_invoice: {},
    };
  },
  metaInfo: {
    title: "Wage Payment Access",
  },
  computed: {
    routeCompanyName() {
      return this.$route.query.company_name || "selected company";
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    getApiNamespace() {
      return this.$route.path.startsWith("/wage-payment-system") ? "wage-payment-system" : "wps";
    },
    invoiceStatusClass(status) {
      return invoice_status[status]?.color || "ds-pill--gray";
    },
    invoiceStatusLabel(status) {
      return invoice_status[status]?.label || status || "—";
    },
    buildQuery() {
      const query = {
        page: this.page,
        size: this.per_page,
        per_page: this.per_page,
        sort: this.filters.sort_type === "desc" ? `-${this.filters.sort_by}` : this.filters.sort_by,
      };

      if (this.filters.search_by && this.filters.search_value) {
        query.search = this.filters.search_by;
        query.search_value = this.filters.search_value;
      }

      if (this.filters.invoice_status) {
        query.invoice_status = this.filters.invoice_status;
      }

      return query;
    },
    searchHandler() {
      this.page = 1;
      this.getList();
    },
    resetHandler() {
      this.filters = {
        search_by: "company_client_name",
        search_value: "",
        invoice_status: "",
        sort_by: "created_at",
        sort_type: "desc",
      };
      this.page = 1;
      this.getList();
    },
    async getList() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const response = await this.$store.dispatch(WPS_INVOICE_LIST_PER_COMPANY, {
          companyId: this.$route.params.id,
          api_namespace: this.getApiNamespace(),
          query: this.buildQuery(),
        });
        const payload = response?.data || response || {};
        const rows = Array.isArray(payload.rows) ? payload.rows : [];

        this.list = rows.map((item) => ({
          ...item,
          company_name: item.company?.company_name || "-",
          company_client_name: item.company_client?.name || "-",
          amount_str: `Rp. ${(item.amount || 0).toLocaleString("id-ID")}`,
          admin_fee_str: `Rp. ${(item.admin_fee || 0).toLocaleString("id-ID")}`,
          start_date_str: item.start_date ? moment(item.start_date).format("DD MMM YYYY") : "—",
          end_date_str: item.end_date ? moment(item.end_date).format("DD MMM YYYY") : "—",
          due_date_str: item.due_date ? moment(item.due_date).format("DD MMM YYYY") : "—",
          paid_at_str: item.paid_at ? moment(item.paid_at).format("DD MMM YYYY") : "—",
        }));
        this.total_rows = payload.count || 0;
      } finally {
        this.is_loading = false;
        this.$parent.hideLoader();
      }
    },
    pageChangeHandler(page) {
      this.page = page;
      this.getList();
    },
    handlerBtnViewInvoice(item) {
      this.show.modal_invoice = true;
      this.selected_invoice = url.resolve(process.env.VUE_APP_VDASHBOARD_API_URL, `api/render?url=${item.invoice_url}`);
    },
    async handlerDownloadInvoice(item) {
      this.$parent.showLoader();
      const invoiceUrl =
        item.overdue && item.overdue.length > 0 ? item.overdue[item.overdue.length - 1].invoice_url : item.invoice_url;
      const split_name = invoiceUrl.split(/\//g);
      const file_name = split_name[split_name.length - 1];
      await fetch(url.resolve(process.env.VUE_APP_VDASHBOARD_API_URL, `api/render?url=${invoiceUrl}`))
        .then((response) => response.blob())
        .then((blob) => {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = file_name;
          link.click();
        });
      this.$parent.hideLoader();
    },
    handlerBtnDetailInvoice(item) {
      this.$router.push({
        name: "Wage Payment System Invoice Detail",
        params: {
          id: this.$route.params.id,
          invoice_id: item.id,
        },
        query: {
          company_name: this.routeCompanyName,
          invoice_number: item.invoice_number || "",
          company_client_name: item.company_client_name || "",
        },
      });
    },
  },
};
</script>
<style scoped>
.wps-filter-header {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.wps-filter-two-col {
  display: grid;
  grid-template-columns: 1fr auto 280px;
  gap: 0 20px;
  align-items: start;
}

.wps-filter-grid-left {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
  align-items: end;
}

.wps-filter-sep {
  width: 1px;
  background: rgba(101, 109, 119, 0.12);
  align-self: stretch;
  margin-top: 2px;
}

.wps-filter-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wps-filter-grid-right {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px 12px;
  align-items: end;
}

.wps-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  margin-top: auto;
}

.wps-action-view.el-button,
.wps-action-download.el-button {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  font-size: 11px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}

.wps-action-view.el-button {
  background: #f4f6fa !important;
  border-color: rgba(101, 109, 119, 0.24) !important;
  color: #656d77 !important;
}

.wps-action-view.el-button:hover {
  background: rgba(101, 109, 119, 0.1) !important;
}

.wps-action-download.el-button {
  background: rgba(32, 107, 196, 0.08) !important;
  border-color: rgba(32, 107, 196, 0.18) !important;
  color: #206bc4 !important;
}

.wps-action-download.el-button:hover {
  background: rgba(32, 107, 196, 0.14) !important;
}

:deep(.el-range-separator) {
  width: 10% !important;
}

@media (max-width: 991px) {
  .wps-filter-header {
    grid-template-columns: 1fr;
  }

  .wps-filter-two-col {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .wps-filter-sep {
    display: none;
  }

  .wps-filter-grid-left {
    grid-template-columns: 1fr;
  }
}
</style>
